import type { BunkSelection } from '../BunkOptions';
import type { PhotoPreviewDraft } from './RoomPhotoPreview';
import type { ConfiguratorSnapshot } from '../design/configurator/integration';
import { BED_OPTIONS } from '../design/configurator/room/beds';
import { photoPurchaseSnapshot } from './photo-purchase-snapshot';

export type PhotoPurchaseView = {
  id: string;
  name: string;
  bunks: BunkSelection[];
  photoPreview?: PhotoPreviewDraft;
  bunkNumbers?: number[];
};
export type PhotoPurchaseRoom = {
  id: string;
  name: string;
  bunks: BunkSelection[];
  photoPreview?: PhotoPreviewDraft;
  views?: PhotoPurchaseView[];
  activeViewId?: string;
};

/** A room may have several photographed spots, but remains one priced room. */
export function roomViews(room: PhotoPurchaseRoom): PhotoPurchaseView[] {
  let offset = 0;
  return (room.views?.length ? room.views : [{ id: 'view-1', name: 'Original spot', bunks: room.bunks, photoPreview: room.photoPreview }]).map(view => {
    const bunkNumbers = view.bunkNumbers ?? view.bunks.map((_, i) => offset + i + 1);
    offset = Math.max(offset, ...bunkNumbers);
    return { ...view, bunkNumbers };
  });
}

export function activeRoomView(room: PhotoPurchaseRoom): PhotoPurchaseView {
  const views = roomViews(room);
  return views.find(v => v.id === room.activeViewId) ?? views[0];
}

export function withRoomViews(room: PhotoPurchaseRoom, views: PhotoPurchaseView[], activeViewId: string): PhotoPurchaseRoom {
  return { ...room, views, activeViewId, photoPreview: undefined, bunks: views.flatMap(v => v.bunks.map((b, i) => ({ b, number: v.bunkNumbers?.[i] ?? i + 1 }))).sort((a,b) => a.number - b.number).map(({b}) => b) };
}

export function updateRoomView(room: PhotoPurchaseRoom, view: PhotoPurchaseView): PhotoPurchaseRoom {
  const previous = activeRoomView(room);
  let number = Math.max(0, ...roomViews(room).flatMap(v => v.bunkNumbers ?? []));
  const bunkNumbers = view.bunks.map((_, i) => {
    // Match saved bed IDs when a bed was removed from the middle of a photo.
    const oldIndex = view.photoPreview && previous.photoPreview ? previous.photoPreview.beds.findIndex(b => b.id === view.photoPreview!.beds[i]?.id) : i;
    return previous.bunkNumbers?.[oldIndex] ?? ++number;
  });
  if (!room.views) return { ...room, bunks: view.bunks, photoPreview: view.photoPreview };
  return withRoomViews(room, roomViews(room).map(v => v.id === view.id ? { ...view, bunkNumbers } : v), view.id);
}

/** Move a selected bunk to its own photo; never duplicate it on the order. */
export function detachViewBunk(room: PhotoPurchaseRoom, bedIndex: number) {
  const view = activeRoomView(room);
  const bunk = view.bunks[bedIndex];
  if (!bunk) throw new Error('Please select a bunk first.');
  const remaining = view.bunks.filter((_, i) => i !== bedIndex);
  const photo = view.photoPreview;
  const beds = photo?.beds.filter((_, i) => i !== bedIndex);
  const active = beds?.find(b => b.id === photo?.active) ?? beds?.[0];
  const views = roomViews(room).flatMap(v => v.id !== view.id ? [v] : remaining.length ? [{ ...view, bunks: remaining, bunkNumbers: view.bunkNumbers?.filter((_,i) => i !== bedIndex), photoPreview: photo && beds && active ? { ...photo, beds, active: active.id, guide: active.placement } : undefined }] : []);
  return { views, bunk, number: view.bunkNumbers![bedIndex] };
}

export function draftBunks(draft: PhotoPreviewDraft): BunkSelection[] {
  return draft.beds.map(b => ({ choice: b.choice, finish: b.finish ?? 'gloss_white', customColorCode: b.customColorCode, upgrades: b.upgrades ?? {} }));
}

export function purchaseRoomSnapshot(room: PhotoPurchaseRoom): ConfiguratorSnapshot {
  const snapshot = photoPurchaseSnapshot(room.bunks.map(b => b.choice), room.bunks.map(b => b.upgrades), room.bunks.map(b => b.finish), room.bunks.map(b => b.customColorCode));
  snapshot.room.height = activeRoomView(room).photoPreview?.ceilingInches ?? 108;
  snapshot.finish = { treatment: 'painted_stained', frameId: 'gloss_white', accentId: 'gloss_white' };
  return snapshot;
}

/** Round-trip existing photo orders and older single-room drafts without re-placing beds. */
export function restorePurchaseRooms(layout: any): PhotoPurchaseRoom[] {
  const entries = layout?.rooms?.length ? layout.rooms : [{ id: 'room-1', name: 'Bunk room 1', configuratorSnapshot: layout?.configuratorSnapshot, photoPreview: layout?.photoPreview }];
  return entries.flatMap((entry: any, index: number) => {
    const views: PhotoPurchaseView[] | undefined = entry.photoViews?.filter((v: PhotoPurchaseView) => v.id && v.bunks?.length && v.bunks.every(b => BED_OPTIONS[b.choice]));
    const draft = entry.photoPreview ?? (index === 0 ? layout?.photoPreview : undefined);
    const snapshot = entry.configuratorSnapshot;
    const bunks: BunkSelection[] = views?.length ? views.flatMap(v => v.bunks) : draft?.beds?.length ? draftBunks(draft) : (snapshot?.beds ?? []).filter((b: any) => !b.kind).map((b: any) => ({
      choice: BED_OPTIONS.findIndex(o => o.name.toLowerCase() === b.name?.toLowerCase()),
      finish: b.basicFinish ?? 'gloss_white',
      customColorCode: b.basicColorCode,
      upgrades: snapshot.basicUpgrades?.find((u: any) => u.bedId === b.id) ?? {},
    }));
    if (!bunks.length || bunks.some(b => !BED_OPTIONS[b.choice])) return [];
    const room = { id: entry.id || `room-${index + 1}`, name: entry.name || `Bunk room ${index + 1}`, bunks, photoPreview: draft ?? undefined };
    return [views?.length ? withRoomViews(room, views, entry.activePhotoViewId ?? views[0].id) : room];
  });
}

export function savedPurchaseRooms(rooms: PhotoPurchaseRoom[]): { id: string; name: string; configuratorSnapshot: ConfiguratorSnapshot; photoPreview?: PhotoPreviewDraft; photoViews?: PhotoPurchaseView[]; activePhotoViewId?: string }[] {
  return rooms.map(room => ({ id: room.id, name: room.name, configuratorSnapshot: purchaseRoomSnapshot(room), ...(room.views ? { photoViews: room.views, activePhotoViewId: room.activeViewId } : room.photoPreview ? { photoPreview: room.photoPreview } : {}) }));
}
